/**
 * cloudImageUpload.ts
 *
 * Uploads images via Supabase Edge Function (uses service role key, bypasses RLS).
 * Falls back to direct client upload if edge function fails.
 */

import { supabase, SUPABASE_ANON_KEY } from "@/lib/supabaseClient";

export interface UploadResult {
  success: boolean;
  url?: string;
  error?: string;
  method?: string;
}

// The user's real Supabase project edge function URL
// Note: edge function must be deployed to this project
const EDGE_FUNCTION_URL = "https://vankfsmglinsfmjijpsc.supabase.co/functions/v1/upload-image";

/**
 * Convert a data URL string to base64 (without the data: prefix).
 */
function dataUrlToBase64(dataUrl: string): string {
  return dataUrl.split(",")[1];
}

/**
 * Convert a data URL to a File object.
 */
function dataUrlToFile(dataUrl: string): File {
  const mimeMatch = dataUrl.match(/data:(.*?);/);
  const contentType = mimeMatch?.[1] ?? "image/jpeg";
  const ext = contentType.split("/")[1]?.replace("jpeg", "jpg") ?? "jpg";
  const fileName = `upload-${Date.now()}.${ext}`;

  const byteString = atob(dataUrl.split(",")[1]);
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  return new File([ab], fileName, { type: contentType });
}

/**
 * Upload via edge function (service role key, bypasses RLS).
 */
async function uploadViaEdgeFunction(file: File): Promise<UploadResult> {
  try {
    // Read file as base64
    const base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        resolve(dataUrlToBase64(result));
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    const response = await fetch(EDGE_FUNCTION_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({
        base64,
        fileName: `uploads/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9.-]/g, "_")}`,
        contentType: file.type || "image/jpeg",
      }),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      return {
        success: false,
        error: data.error || `Edge function error: ${response.status}`,
        method: "edge-function",
      };
    }

    return { success: true, url: data.url, method: "edge-function" };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : "Edge function request failed",
      method: "edge-function",
    };
  }
}

/**
 * Fallback: upload directly via Supabase client (may fail due to RLS).
 */
async function uploadViaClient(file: File): Promise<UploadResult> {
  try {
    const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, "_");
    const filePath = `uploads/${Date.now()}-${safeName}`;

    const { error: uploadError } = await supabase.storage
      .from("website-images")
      .upload(filePath, file, { cacheControl: "3600", upsert: false });

    if (uploadError) {
      return {
        success: false,
        error: `Direct upload failed: ${uploadError.message}`,
        method: "supabase-direct",
      };
    }

    const { data: urlData } = supabase.storage
      .from("website-images")
      .getPublicUrl(filePath);

    return { success: true, url: urlData.publicUrl, method: "supabase-direct" };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : "Direct upload error",
      method: "supabase-direct",
    };
  }
}

/**
 * Upload a file or data URL.
 * Tries edge function first, falls back to direct client upload.
 */
export async function uploadImageToCloudDetailed(fileOrDataUrl: File | string): Promise<UploadResult> {
  let file: File;

  if (typeof fileOrDataUrl === "string") {
    if (!fileOrDataUrl.startsWith("data:")) {
      return { success: true, url: fileOrDataUrl, method: "url" };
    }
    file = dataUrlToFile(fileOrDataUrl);
  } else {
    file = fileOrDataUrl;
  }

  // Try edge function first (bypasses RLS)
  const edgeResult = await uploadViaEdgeFunction(file);
  if (edgeResult.success) {
    return edgeResult;
  }

  // Fallback: direct client upload
  const directResult = await uploadViaClient(file);
  if (directResult.success) {
    return directResult;
  }

  // Both failed — return edge error as primary, include fallback info
  return {
    success: false,
    error: `${edgeResult.error} (fallback also failed: ${directResult.error})`,
    method: "edge-function",
  };
}

/**
 * Simple wrapper that throws on failure.
 */
export async function uploadImageToCloud(fileOrDataUrl: File | string): Promise<string> {
  const result = await uploadImageToCloudDetailed(fileOrDataUrl);
  if (!result.success) {
    throw new Error(result.error ?? "Upload failed");
  }
  return result.url!;
}

export async function uploadFileToCloud(file: File): Promise<string> {
  return uploadImageToCloud(file);
}

/**
 * Test if the cloud upload system is working.
 */
export async function testCloudUpload(): Promise<{
  ok: boolean;
  url?: string;
  error?: string;
  details?: UploadResult;
}> {
  const tinyPng =
    "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8z8BQDwAEhQGAhKmMIQAAAABJRU5ErkJggg==";
  const result = await uploadImageToCloudDetailed(tinyPng);
  if (result.success) {
    return { ok: true, url: result.url, details: result };
  }
  return { ok: false, error: result.error, details: result };
}
